import * as React from 'react'
import { Box, Typography } from '@mui/material'
import { keyframes } from '@emotion/react'
import { aceColors, aceConvention } from '../../data/aceConvention'

const idlePulse = keyframes`
  0% { transform: scale(1); opacity: 0.55; }
  70% { transform: scale(1.55); opacity: 0; }
  100% { transform: scale(1.55); opacity: 0; }
`

const gongReverb = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  100% { transform: scale(2.4); opacity: 0; }
`

const gongStrike = keyframes`
  0% { transform: scale(1) rotate(0deg); }
  12% { transform: scale(0.92) rotate(-2deg); }
  28% { transform: scale(1.08) rotate(2deg); }
  45% { transform: scale(0.97) rotate(-1deg); }
  62% { transform: scale(1.03) rotate(1deg); }
  80% { transform: scale(0.99) rotate(0deg); }
  100% { transform: scale(1) rotate(0deg); }
`

const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`

/**
 * Shazam-style circular control that triggers the physical gong webhook
 * and plays a reverberating strike animation on each press.
 */
const AceGongButton = ({ visitor, onHit, onExhausted, remainingHits }) => {
  const [striking, setStriking] = React.useState(false)
  const [ripples, setRipples] = React.useState([])
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const rippleId = React.useRef(0)
  const remainingRef = React.useRef(remainingHits)

  React.useEffect(() => {
    remainingRef.current = remainingHits
  }, [remainingHits])

  const fireWebhook = async () => {
    const res = await fetch(aceConvention.gongWebhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event: 'ace_gong_hit',
        firstName: visitor.name,
        lastName: '—',
        name: visitor.name,
        email: visitor.email,
        store_location: visitor.storeLocation,
        company: visitor.storeLocation,
        campaign: aceConvention.campaign,
        source: aceConvention.gongLeadSource,
        lead_source: aceConvention.gongLeadSource,
        page: aceConvention.routes.gong,
        message: `Gong hit from ${visitor.storeLocation}`,
        gong_access: true,
        timestamp: new Date().toISOString(),
      }),
    })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(text || `Gong request failed (${res.status})`)
    }
  }

  const handlePress = async () => {
    if (busy || remainingRef.current <= 0) return
    setError('')
    setBusy(true)
    setStriking(true)

    const id = rippleId.current++
    setRipples((prev) => [...prev, id])
    window.setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r !== id))
    }, 900)
    window.setTimeout(() => setStriking(false), 700)

    try {
      await fireWebhook()
      const nextRemaining = remainingRef.current - 1
      remainingRef.current = nextRemaining
      onHit?.(nextRemaining)
      if (nextRemaining <= 0) {
        onExhausted?.()
      }
    } catch (err) {
      setError(err.message || 'Could not ring the gong. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: 420,
        mx: 'auto',
      }}
    >
      <Typography
        component="h1"
        sx={{
          textAlign: 'center',
          fontWeight: 900,
          fontSize: { xs: '1.85rem', sm: '2.4rem' },
          color: '#fff',
          letterSpacing: '-0.02em',
          mb: 1,
          lineHeight: 1.1,
        }}
      >
        Hit the gong
      </Typography>
      <Typography
        sx={{
          textAlign: 'center',
          color: 'rgba(255,255,255,0.78)',
          mb: 4,
          lineHeight: 1.55,
          maxWidth: 340,
        }}
      >
        Press the button to ring the booth gong. Mystery hits remaining—listen for the real thing.
      </Typography>

      <Box
        sx={{
          position: 'relative',
          width: { xs: 240, sm: 280 },
          height: { xs: 240, sm: 280 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Idle Shazam-style concentric rings */}
        {[0, 1, 2].map((i) => (
          <Box
            key={`idle-${i}`}
            aria-hidden
            sx={{
              position: 'absolute',
              inset: `${12 + i * 10}%`,
              borderRadius: '50%',
              border: `2px solid ${aceColors.blueSoft}`,
              animation: `${idlePulse} 2.4s ease-out infinite`,
              animationDelay: `${i * 0.55}s`,
              opacity: striking ? 0.15 : undefined,
              pointerEvents: 'none',
            }}
          />
        ))}

        {/* Strike reverberation ripples */}
        {ripples.map((id) => (
          <Box
            key={id}
            aria-hidden
            sx={{
              position: 'absolute',
              inset: '8%',
              borderRadius: '50%',
              border: `3px solid ${aceColors.red}`,
              boxShadow: `0 0 24px ${aceColors.red}88`,
              animation: `${gongReverb} 0.9s ease-out forwards`,
              pointerEvents: 'none',
            }}
          />
        ))}

        <Box
          component="button"
          type="button"
          aria-label="Ring the gong"
          disabled={busy || remainingHits <= 0}
          onClick={handlePress}
          sx={{
            position: 'relative',
            zIndex: 1,
            width: { xs: 148, sm: 168 },
            height: { xs: 148, sm: 168 },
            borderRadius: '50%',
            border: 'none',
            cursor: busy || remainingHits <= 0 ? 'wait' : 'pointer',
            color: '#fff',
            fontWeight: 900,
            fontSize: { xs: '1.15rem', sm: '1.3rem' },
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            background: `linear-gradient(145deg, ${aceColors.red} 0%, ${aceColors.redDeep} 45%, #8B1A18 100%)`,
            backgroundSize: '200% 200%',
            animation: striking
              ? `${gongStrike} 0.7s cubic-bezier(0.22, 1, 0.36, 1)`
              : `${shimmer} 4s ease infinite`,
            boxShadow: `
              0 0 0 6px rgba(255,255,255,0.08),
              0 0 0 14px rgba(229,57,53,0.18),
              0 18px 40px -12px rgba(0,0,0,0.55),
              inset 0 2px 0 rgba(255,255,255,0.25)
            `,
            transition: 'filter 0.2s ease',
            '&:hover:not(:disabled)': {
              filter: 'brightness(1.08)',
            },
            '&:disabled': {
              opacity: 0.85,
            },
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          GONG
        </Box>
      </Box>

      {error ? (
        <Typography sx={{ mt: 3, color: aceColors.red, textAlign: 'center', fontWeight: 700 }}>
          {error}
        </Typography>
      ) : (
        <Typography
          sx={{
            mt: 3,
            color: 'rgba(255,255,255,0.55)',
            fontFamily: '"Caveat", "Segoe Print", cursive',
            fontWeight: 700,
            fontSize: '1.35rem',
            textAlign: 'center',
          }}
        >
          Tap to reverberate
        </Typography>
      )}
    </Box>
  )
}

export default AceGongButton
